import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';

interface WarehouseListItem {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly timezone: string;
  readonly address: string | null;
  readonly status: 'ACTIVE' | 'ARCHIVED';
  readonly version: number;
}

/** UX 设计书 S15："组织"分区（需 settings.manage）——仓库管理是其中一项 */
export function WarehousesPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => apiFetch<{ items: WarehouseListItem[] }>('/warehouses'),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('Asia/Shanghai');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 【用户反馈】顶尖账号此前没有任何改仓库名的入口——PATCH 端点早已存在，
  // 这里补上编辑弹窗（名称/时区，乐观锁 version）
  const [editTarget, setEditTarget] = useState<WarehouseListItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editTimezone, setEditTimezone] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['warehouses'] });

  const startEdit = (w: WarehouseListItem): void => {
    setEditTarget(w);
    setEditName(w.name);
    setEditTimezone(w.timezone);
    setError(null);
  };

  const saveEdit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!editTarget) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/warehouses/${editTarget.id}`, {
        method: 'PATCH',
        body: { name: editName, timezone: editTimezone, version: editTarget.version },
      });
      setEditTarget(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch('/warehouses', { method: 'POST', body: { code, name, timezone } });
      setShowCreate(false);
      setCode('');
      setName('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const archive = async (w: WarehouseListItem): Promise<void> => {
    await apiFetch(`/warehouses/${w.id}/archive`, { method: 'POST' });
    await refresh();
  };

  return (
    <section className="section">
      <div className="row row--between">
        <h2>{t('warehouses.manageTitle')}</h2>
        <button className="btn btn--primary btn--sm" onClick={() => setShowCreate(true)}>
          {t('warehouses.create')}
        </button>
      </div>

      {isPending && <p>{t('app.loading')}</p>}
      {data && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('warehouses.colCode')}</th>
              <th>{t('warehouses.colName')}</th>
              <th>{t('warehouses.colTimezone')}</th>
              <th>{t('users.colStatus')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((w) => (
              <tr key={w.id}>
                <td>{w.code}</td>
                <td>{w.name}</td>
                <td>{w.timezone}</td>
                <td>
                  <span
                    className={`badge ${w.status === 'ACTIVE' ? 'badge--success' : 'badge--muted'}`}
                  >
                    {w.status}
                  </span>
                </td>
                <td>
                  <span className="row" style={{ gap: 'var(--space-1)' }}>
                    <button className="btn btn--ghost btn--sm" onClick={() => startEdit(w)}>
                      {t('warehouses.edit')}
                    </button>
                    {w.status === 'ACTIVE' && (
                      <button className="btn btn--ghost btn--sm" onClick={() => void archive(w)}>
                        {t('warehouses.archive')}
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void saveEdit(e)}
          >
            <h2>{t('warehouses.editTitle', { code: editTarget.code })}</h2>
            <label className="field">
              <span>{t('warehouses.colName')}</span>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                autoFocus
                maxLength={200}
              />
            </label>
            <label className="field">
              <span>{t('warehouses.colTimezone')}</span>
              <input
                value={editTimezone}
                onChange={(e) => setEditTimezone(e.target.value)}
                required
                maxLength={100}
              />
            </label>
            {error && <p className="field-error">{error}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setEditTarget(null)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {t('app.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void create(e)}
          >
            <h2>{t('warehouses.create')}</h2>
            <label className="field">
              <span>{t('warehouses.colCode')}</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
            </label>
            <label className="field">
              <span>{t('warehouses.colName')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label className="field">
              <span>{t('warehouses.colTimezone')}</span>
              <input value={timezone} onChange={(e) => setTimezone(e.target.value)} required />
            </label>
            {error && <p className="field-error">{error}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowCreate(false)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {t('warehouses.create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
